import { encolarImpresion } from "./colaImpresion";
import { RESULTADO_COMANDA_REMOTA, emitirComandaRemota, lineasComandaRemota } from "./comandaRemota";
import { renderComanda80mm } from "./renderComanda80mm";
import { renderConstanciaBonificada80mm, renderFactura80mm } from "./renderFactura80mm";

// Estados que la pantalla puede afirmar con honestidad. No existe "impresa":
// el navegador sólo sabe que solicitó el trabajo. En la comanda remota
// SOLICITADA sí significa que el print server respondió 200.
export const ESTADO_IMPRESION = Object.freeze({
  SIN_SOLICITAR: "SIN_SOLICITAR",
  PREPARANDO: "PREPARANDO",
  SOLICITADA: "SOLICITADA",
  ERROR_LOCAL: "ERROR_LOCAL",
  NO_APLICA: "NO_APLICA",
  PENDIENTE_AFIP: "PENDIENTE_AFIP",
});

export const ESTADO_FISCAL = Object.freeze({
  FACTURADA: "FACTURADA",
  NO_APLICA_TOTAL_CERO: "NO_APLICA_TOTAL_CERO",
  PENDIENTE_AFIP: "PENDIENTE_AFIP",
});

export const TIPO_DOCUMENTO = Object.freeze({
  FACTURA: "FACTURA",
  CONSTANCIA: "CONSTANCIA",
  COMANDA: "COMANDA",
});

// Las ranuras de la venta: el primer papel (factura o constancia), la comanda
// local y la comanda remota a cafetería. Son las claves de los estados que
// muestran los diálogos.
export const RANURA = Object.freeze({
  FACTURA: "factura",
  COMANDA: "comanda",
  COMANDA_REMOTA: "comandaRemota",
});

// Qué va en la primera ranura según lo persistido. Null cuando no hay nada
// legítimo que imprimir todavía.
export const resolverPrimerDocumento = (documentos) => {
  switch (documentos?.estadoFiscal) {
    case ESTADO_FISCAL.FACTURADA:
      return documentos.factura ? TIPO_DOCUMENTO.FACTURA : null;
    case ESTADO_FISCAL.NO_APLICA_TOTAL_CERO:
      return TIPO_DOCUMENTO.CONSTANCIA;
    default:
      return null;
  }
};

export const estadoInicialPrimerDocumento = (documentos) => {
  if (!documentos) return ESTADO_IMPRESION.NO_APLICA;
  if (documentos.estadoFiscal === ESTADO_FISCAL.PENDIENTE_AFIP) return ESTADO_IMPRESION.PENDIENTE_AFIP;
  return resolverPrimerDocumento(documentos) ? ESTADO_IMPRESION.SIN_SOLICITAR : ESTADO_IMPRESION.NO_APLICA;
};

export const estadoInicialComanda = (documentos) =>
  documentos?.lineas?.length ? ESTADO_IMPRESION.SIN_SOLICITAR : ESTADO_IMPRESION.NO_APLICA;

// Un pedido 100 % menú universitario no tiene nada para cafetería.
export const estadoInicialComandaRemota = (documentos) =>
  lineasComandaRemota(documentos).length ? ESTADO_IMPRESION.SIN_SOLICITAR : ESTADO_IMPRESION.NO_APLICA;

export const estadosIniciales = (documentos) => ({
  [RANURA.FACTURA]: estadoInicialPrimerDocumento(documentos),
  [RANURA.COMANDA]: estadoInicialComanda(documentos),
  [RANURA.COMANDA_REMOTA]: estadoInicialComandaRemota(documentos),
});

const renderPorTipo = {
  [TIPO_DOCUMENTO.FACTURA]: renderFactura80mm,
  [TIPO_DOCUMENTO.CONSTANCIA]: renderConstanciaBonificada80mm,
  [TIPO_DOCUMENTO.COMANDA]: renderComanda80mm,
};

// Prepara y encola un documento. Devuelve el estado final de esa solicitud;
// nunca lanza: un error local se informa como estado para que la pantalla
// ofrezca reintentar.
export const imprimirDocumento = async (documentos, tipo) => {
  const render = renderPorTipo[tipo];
  if (!render) return { estado: ESTADO_IMPRESION.ERROR_LOCAL, error: `Documento desconocido: ${tipo}` };
  try {
    const html = await render(documentos);
    await encolarImpresion({ html });
    return { estado: ESTADO_IMPRESION.SOLICITADA, error: null };
  } catch (error) {
    return {
      estado: ESTADO_IMPRESION.ERROR_LOCAL,
      error: error?.message || "No se pudo solicitar la impresión",
    };
  }
};

// Emite la comanda remota y traduce su resultado a los estados de la pantalla.
// Nunca lanza.
export const imprimirComandaRemota = async (documentos) => {
  const { resultado, error } = await emitirComandaRemota(documentos);
  if (resultado === RESULTADO_COMANDA_REMOTA.EMITIDA) return { estado: ESTADO_IMPRESION.SOLICITADA, error: null };
  if (resultado === RESULTADO_COMANDA_REMOTA.NO_APLICA) return { estado: ESTADO_IMPRESION.NO_APLICA, error: null };
  return { estado: ESTADO_IMPRESION.ERROR_LOCAL, error };
};

// Reimpresión manual de una ranura. Para la primera decide factura o
// constancia según el estado fiscal; con PENDIENTE_AFIP no fabrica nada.
export const imprimirRanura = async (documentos, ranura) => {
  if (ranura === RANURA.COMANDA_REMOTA) return imprimirComandaRemota(documentos);
  if (ranura === RANURA.COMANDA) {
    if (!documentos?.lineas?.length) return { estado: ESTADO_IMPRESION.NO_APLICA, error: null };
    return imprimirDocumento(documentos, TIPO_DOCUMENTO.COMANDA);
  }
  if (documentos?.estadoFiscal === ESTADO_FISCAL.PENDIENTE_AFIP) {
    return { estado: ESTADO_IMPRESION.PENDIENTE_AFIP, error: null };
  }
  const tipo = resolverPrimerDocumento(documentos);
  if (!tipo) return { estado: ESTADO_IMPRESION.NO_APLICA, error: null };
  return imprimirDocumento(documentos, tipo);
};

// Camino automático al cerrar una venta: primer papel, comanda local y, al
// final, la comanda remota. Un error de un papel no impide intentar los
// siguientes.
// onEstado(ranura, estado, error) avisa cada transición. omitirComandaRemota la
// deja en NO_APLICA (modo demo: no se mandan trabajos a la comandera real).
export const imprimirDocumentosVenta = async (documentos, { onEstado, omitirComandaRemota = false } = {}) => {
  const avisar = (ranura, estado, error = null) => {
    if (typeof onEstado === "function") onEstado(ranura, estado, error);
  };
  const resultado = estadosIniciales(documentos);
  if (omitirComandaRemota) resultado[RANURA.COMANDA_REMOTA] = ESTADO_IMPRESION.NO_APLICA;

  const tipoPrimero = resolverPrimerDocumento(documentos);
  if (tipoPrimero) {
    avisar(RANURA.FACTURA, ESTADO_IMPRESION.PREPARANDO);
    const primero = await imprimirDocumento(documentos, tipoPrimero);
    resultado[RANURA.FACTURA] = primero.estado;
    avisar(RANURA.FACTURA, primero.estado, primero.error);
  } else {
    avisar(RANURA.FACTURA, resultado[RANURA.FACTURA]);
  }

  if (documentos?.lineas?.length) {
    avisar(RANURA.COMANDA, ESTADO_IMPRESION.PREPARANDO);
    const comanda = await imprimirDocumento(documentos, TIPO_DOCUMENTO.COMANDA);
    resultado[RANURA.COMANDA] = comanda.estado;
    avisar(RANURA.COMANDA, comanda.estado, comanda.error);
  } else {
    avisar(RANURA.COMANDA, resultado[RANURA.COMANDA]);
  }

  if (resultado[RANURA.COMANDA_REMOTA] === ESTADO_IMPRESION.SIN_SOLICITAR) {
    avisar(RANURA.COMANDA_REMOTA, ESTADO_IMPRESION.PREPARANDO);
    const remota = await imprimirComandaRemota(documentos);
    resultado[RANURA.COMANDA_REMOTA] = remota.estado;
    avisar(RANURA.COMANDA_REMOTA, remota.estado, remota.error);
  } else {
    avisar(RANURA.COMANDA_REMOTA, resultado[RANURA.COMANDA_REMOTA]);
  }

  return resultado;
};

export { renderComanda80mm, renderConstanciaBonificada80mm, renderFactura80mm };
